/**
 * Editorial authority essays for individual songs.
 *
 * Keyed by `${releaseSlug}/${songSlug}`. Purely editorial: no facts, credits,
 * durations, ISRCs, or streaming destinations are asserted here — those live
 * only in the canonical release/song records.
 */
import type { SongEssay, SongEssaySection, SongCompanion } from "./song-essays/types";
import { DIAMOND_SUTRA_ESSAYS_1_6 } from "./song-essays/diamond-sutra-1-6";
import { DIAMOND_SUTRA_ESSAYS_8_12 } from "./song-essays/diamond-sutra-8-12";
import { DIAMOND_SUTRA_ESSAYS_13_17 } from "./song-essays/diamond-sutra-13-17";

export type { SongEssay, SongEssaySection, SongCompanion };

const WHO_TAUGHT_YOU_TO_WANT: SongEssay = {
  seoTitle: "Who Taught You to Want? — Buddha KA | The Diamond Sutra",
  seoDescription:
    "Buddha KA's “Who Taught You to Want?” traces desire back to its source — the culture, repetition, and inherited belief that trained the mind to assign value. From The Diamond Sutra, released August 19, 2026.",
  standfirst:
    "A study of where desire is manufactured — and whether the wanting was ever yours to begin with.",
  sections: [
    {
      eyebrow: "About the song",
      title: "About the Song",
      paragraphs: [
        "“Who Taught You to Want?” does not argue with desire. It examines the factory. Most conversations about wanting stay at the level of the object — the house, the recognition, the body, the number. Buddha KA moves one step upstream and asks a colder question: who installed the appetite?",
        "The song treats desire as an inherited technology. Culture repeats an image until the nervous system reads it as necessity. Advertising rents attention and returns it with a hunger attached. Family patterns hand down not only values but the ache that enforces them. Status systems teach the mind what to compare itself against before it has decided what it loves.",
        "None of this is presented as an attack on ambition. The record does not ask anyone to want less. It asks that wanting be examined with the same seriousness a person would give to any other instrument they intend to live by.",
      ],
    },
    {
      eyebrow: "The inquiry",
      title: "The Question Under the Question",
      paragraphs: [
        "Underneath “what do I want?” lies a second question that is rarely spoken aloud: who taught me that this was worth wanting? The first question flatters the ego by assuming authorship. The second returns the mind to evidence.",
        "This is where Buddhist philosophical inquiry and ordinary self-awareness meet. Attachment is not only the grip on a thing; it is the belief that the grip originated in us. Trace one want honestly and it frequently arrives with a witness attached — a voice, a room, a moment of comparison, a fear of being unchosen.",
        "The song's discipline is refusal of the easy answer. It does not conclude that all desire is false. It concludes that unexamined desire is unowned, and that anything unowned in a life will eventually run it.",
      ],
    },
    {
      eyebrow: "The method",
      title: "Mirror Thinking",
      paragraphs: [
        "Mirror Thinking is Buddha KA's working method for exactly this kind of examination. Rather than answering a thought, it turns the thought around to see what is behind it. Instead of asking only what someone wants, Mirror Thinking asks who taught the mind to assign value to that thing in the first place.",
        "Applied to desire, the mirror works in a sequence: name the want plainly, locate the earliest memory of it, identify whose approval is quietly attached, and then test whether the want survives without that audience. Whatever remains after that test is closer to the person's own.",
        "The practice is not renunciation. It is authorship. A want that survives examination becomes usable — direction rather than compulsion. A want that dissolves under examination was never carrying the person forward; it was collecting a debt on someone else's behalf.",
      ],
    },
    {
      eyebrow: "Context",
      title: "Inside The Diamond Sutra",
      paragraphs: [
        "The Diamond Sutra is an album-length investigation of perception, identity, conditioned thought, and the structures operating underneath ordinary consciousness. Its subject is not belief but the machinery that produces belief.",
        "“Who Taught You to Want?” sits at the center of that investigation as the desire chapter. Where other pieces on the record examine how the mind sees and how it names itself, this one examines what it reaches for — and why the reaching feels involuntary.",
        "It shares a bloodline with “Borrowed Minds,” which studies acquired mental structures directly. Both records treat the interior as inherited architecture: rooms someone else built, furniture someone else chose, and a resident who has never once asked who signed the lease.",
      ],
    },
    {
      eyebrow: "Listening",
      title: "What to Listen For",
      paragraphs: [
        "Listen for the shift in who is speaking. The song moves between the voice of the conditioned want and the voice examining it, and the distance between them is the point.",
        "Listen for repetition used as evidence rather than hook. Conditioning is built by repetition, and the record uses that same mechanism to let a listener feel how quickly a phrase becomes a belief.",
        "Listen for the places where the music withdraws. Attention is the real subject beneath desire — what a person is willing to keep looking at is what eventually becomes their life. The silences are where the question is handed back to the listener.",
      ],
    },
  ],
  companion: {
    songSlug: "borrowed-minds",
    title: "Borrowed Minds",
    reason: "Its companion study — how acquired mental structures move in as though they were native.",
  },
};

const DIAMOND_SUTRA: Record<string, SongEssay> = {
  ...DIAMOND_SUTRA_ESSAYS_1_6,
  "who-taught-you-to-want": WHO_TAUGHT_YOU_TO_WANT,
  ...DIAMOND_SUTRA_ESSAYS_8_12,
  ...DIAMOND_SUTRA_ESSAYS_13_17,
};

export const SONG_ESSAYS: Record<string, SongEssay> = Object.fromEntries(
  Object.entries(DIAMOND_SUTRA).map(([slug, essay]) => [`the-diamond-sutra/${slug}`, essay]),
);

export function getSongEssay(releaseSlug: string, songSlug: string | undefined): SongEssay | null {
  if (!songSlug) return null;
  return SONG_ESSAYS[`${releaseSlug}/${songSlug}`] ?? null;
}
